import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, 
  // ToastContainer 
} from "react-toastify";
import Layout from "../common/components/Layout";
import "react-toastify/dist/ReactToastify.css";

import { 
  fetchImportHistory, 
  fetchBatchDetails, 
  clearDetails 
} from "../store/slices/importLogSlice.js";

const ImportHistory = () => {
  const dispatch = useDispatch();
  
  const { 
    history = [], 
    totalCount = 0, 
    historyLoading, 
    selectedBatchDetails = [], 
    detailsLoading 
  } = useSelector((state) => state.employeeImport);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const skip = (currentPage - 1) * itemsPerPage;
    dispatch(fetchImportHistory({ limit: itemsPerPage, skip }));
  }, [dispatch, currentPage]);

  const handleViewDetails = async (batchId) => {
    try {
      await dispatch(fetchBatchDetails(batchId)).unwrap();
      setShowDetailModal(true);
    } catch (err) {
      toast.error("Failed to load batch details");
    }
  };

  const closeDetails = () => {
    setShowDetailModal(false);
    dispatch(clearDetails());
  };

  const downloadErrorCSV = () => {
    if (!Array.isArray(selectedBatchDetails) || selectedBatchDetails.length === 0) return;

    // Filter by 'FAILED' AND 'PARTIAL SUCCESS' status
    const problematicRows = selectedBatchDetails.filter(row => 
        row.status === 'FAILED' || row.status === 'PARTIAL SUCCESS'
    );
    
    if (problematicRows.length === 0) return toast.info("No errors to download!");

    const headers = ["Row", "Employee", "Status", "Error Message"];
    const rows = problematicRows.map(item => {
      const fName = item.rawData?.["First Name"] || item.rawData?.["firstname"] || "";
      const lName = item.rawData?.["Last Name"] || item.rawData?.["lastname"] || "";
      const fullName = `${fName} ${lName}`.trim() || 'N/A';
      
      return [
        item.rowNumber, 
        `"${fullName}"`, 
        item.status, 
        `"${item.errors?.replace(/"/g, '""') || 'Unknown Error'}"`
      ];
    });

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `import_report_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.ceil((totalCount || 0) / itemsPerPage);

  // Helper to render badges in modal
  const renderStatusBadge = (status) => {
    let bgClass = "bg-danger";
    let label = status;

    if (status === 'SUCCESS' || status === 'COMPLETED') bgClass = "bg-success";
    if (status === 'PARTIAL SUCCESS') bgClass = "bg-warning text-dark";

    return (
      <span className={`badge badge-fixed-width ${bgClass}`} style={{ fontSize: '0.6rem' }}>
        {label}
      </span>
    );
  };

  return (
    <Layout>
      {/* <style>{`
        .btn-text-action { background: none; border: none; color: #dc2626; font-size: 0.75rem; font-weight: 700; text-decoration: underline; cursor: pointer; padding: 0; }
        .modal-footer-custom { padding: 1rem 1.5rem; background: #f8fafc; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
        .table-danger-subtle { background-color: #fff1f2 !important; }
        .table-warning-subtle { background-color: #fffbeb !important; }
        .badge-fixed-width { width: 95px; display: inline-block; }
        .text-xs { font-size: 0.75rem; }
      `}</style>
      
      <ToastContainer position="top-right" autoClose={3000} /> */}

      <div className="container-fluid py-4 px-lg-5">
        <div className="mb-4 d-flex justify-content-between align-items-end">
          <div>
            <h2 className="fw-bold m-0">Import History</h2>
            <p className="text-muted small mb-0">Track and review your employee data uploads</p>
          </div>
          <div className="text-muted small">
            Total Logs: <strong>{totalCount}</strong>
          </div>
        </div>

        <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="ps-4">Date</th>
                  <th className="ps-4">ID</th>
                  <th className="ps-4">Module</th>
                  <th className="ps-4">File Name</th>
                  <th className="text-center">Results</th>
                  <th className="text-end pe-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan="4" className="text-center py-5">
                      <div className="spinner-border spinner-border-sm text-primary me-2"></div> 
                      Fetching history...
                    </td>
                  </tr>
                ) : (Array.isArray(history) && history.length > 0) ? (
                  history.map((item) => (
                    <tr key={item.id} className="align-middle">
                      <td className="ps-4 text-nowrap">{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td className="ps-4 text-nowrap">{item.id}</td>
                      <td className="ps-4 text-nowrap">{item.moduleName}</td>
                      <td className="fw-medium text-truncate" style={{ maxWidth: '250px' }}>{item.fileName}</td>
                      <td className="text-center">
                        <span className="badge bg-success-subtle text-success me-2">{item.successCount} Success</span>
                        <span className="badge bg-warning-subtle text-warning me-2">{item.partialCount || 0} Partial</span>
                        <span className="badge bg-danger-subtle text-danger">{item.failureCount} Failed</span>
                      </td>
                      <td className="text-end pe-4">
                        <button className="btn btn-sm btn-outline-primary px-3 fw-bold" onClick={() => handleViewDetails(item.id)}>
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center py-5 text-muted">No import logs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          <div className="card-footer bg-white d-flex justify-content-between py-3 border-top">
            <small className="text-muted">Page {currentPage} of {totalPages || 1}</small>
            <div className="btn-group">
              <button 
                className="btn btn-sm btn-outline-dark px-3" 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(p => p - 1)}
              >Prev</button>
              <button 
                className="btn btn-sm btn-outline-dark px-3" 
                disabled={currentPage >= totalPages || totalPages === 0} 
                onClick={() => setCurrentPage(p => p + 1)}
              >Next</button>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1050 }}>
          <div className="bg-white rounded-4 shadow-lg overflow-hidden animate__animated animate__zoomIn animate__faster" style={{ width: "850px", maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            
            <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-white">
              <h6 className="mb-0 fw-bold text-uppercase" style={{ fontSize: '0.75rem', color: '#64748b' }}>Detailed Batch Report</h6>
              <button className="btn-close" onClick={closeDetails}></button>
            </div>
            
            <div className="p-4 overflow-auto">
              {detailsLoading ? (
                  <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : (
                <table className="table table-sm align-middle">
                  <thead className="small text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>
                    <tr>
                      <th style={{ width: '50px' }}>Row</th>
                      <th style={{ width: '200px' }}>Employee</th>
                      <th style={{ width: '100px' }}>Status</th>
                      <th>Reason / Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(selectedBatchDetails) && selectedBatchDetails.length > 0 ? (
                      selectedBatchDetails.map((item, i) => {
                        const firstName = item.rawData?.["First Name"] || item.rawData?.["firstname"] || "";
                        const lastName = item.rawData?.["Last Name"] || item.rawData?.["lastname"] || "";
                        const fullName = `${firstName} ${lastName}`.trim() || 'N/A';

                        let rowClass = "";
                        if (item.status === 'FAILED') rowClass = "table-danger-subtle";
                        if (item.status === 'PARTIAL SUCCESS') rowClass = "table-warning-subtle";

                        return (
                          <tr key={item.id || i} className={rowClass}>
                            <td className="fw-bold">{item.rowNumber}</td>
                            <td className="text-nowrap">{fullName}</td>
                            <td>{renderStatusBadge(item.status)}</td>
                            <td className="text-muted text-xs" style={{ lineHeight: '1.4' }}>
                              {item.errors || '-'}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan="4" className="text-center py-4 text-muted">No specific row details available.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="modal-footer-custom">
              <div>
                {Array.isArray(selectedBatchDetails) && selectedBatchDetails.some(r => r.status === 'FAILED' || r.status === 'PARTIAL SUCCESS') && (
                  <button className="btn-text-action" onClick={downloadErrorCSV}>
                    <i className="bi bi-download me-1"></i> Download CSV Issues
                  </button>
                )}
              </div>
              <button className="btn btn-secondary btn-sm px-4 fw-bold rounded-3" onClick={closeDetails}>Done</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ImportHistory;